import { useQuery } from '@tanstack/react-query'
import { ViewCustomer } from '@xeetr/types/models'
import { useContext, useDeferredValue, useState } from 'react'

import { ActiveTenantContext } from '../../context/common/Context'
import { queryKeys } from '../../lib/queryKeys'
import { CustomerService, PaginatedCustomerResponse } from '../../services/Customer.service'
import { useTenantOptionalService } from '../common/useServiceFactory'


interface UseCustomerSearchReturn {
  search: ViewCustomer[]
  searchLoading: boolean
  filter: (query: string) => void
  total: number
  page: number
  limit: number
  setPage: (page: number) => void
  setLimit: (limit: number) => void
}

export const useCustomerSearch = (): UseCustomerSearchReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(50)
  const deferredQuery = useDeferredValue(searchQuery)

  const getCustomerService = useTenantOptionalService(CustomerService)

  const searchQuery_ = useQuery({
    queryKey: queryKeys.customers.filter(tenantId || '', deferredQuery, page, limit),
    queryFn: async () => {
      if (!tenantId || deferredQuery.trim().length <= 2) {
        return { data: [], total: 0, page: 1, limit: 50 } as PaginatedCustomerResponse
      }
      const service = await getCustomerService()
      return service.filterCustomer(deferredQuery, page, limit)
    },
    enabled: !!tenantId && deferredQuery.trim().length > 2,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })

  const result = searchQuery_.data || { data: [], total: 0, page: 1, limit: 50 }

  return {
    search: result.data,
    searchLoading: searchQuery_.isLoading,
    filter: setSearchQuery,
    total: result.total,
    page: result.page,
    limit: result.limit,
    setPage,
    setLimit,
  }
}